import { PaymentMethod } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ContactInfo from '@platform-ui-common/components/ContactInfo/index.js';
import { IconType } from '@platform-ui-common/components/Icon/Icon.types.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import SimpleList from '@platform-ui-common/components/SimpleList/index.js';
import { SimpleListItem } from '@platform-ui-common/components/SimpleList/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import UsioContact from '@platform-ui-common/components/UsioContact/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import Color from '@platform-ui-common/utilities/Color.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import cn from 'classnames';
import { ReactNode } from 'react';
import { useHistory } from 'react-router';

const submissionContent = (
  partnerEmail: string,
): Record<
  Exclude<PaymentMethod, PaymentMethod.External>,
  { header: string; contents: ReactNode[]; icon?: IconType }
> => ({
  [PaymentMethod.DirectDeposit]: {
    header: 'Your transfer has been successfully initiated',
    contents: [
      'Your transfer has been initiated. Your funds should be available in your account in 1-3 business days.',
    ],
  },
  [PaymentMethod.Check]: {
    header: 'You have successfully submitted your mailing address',
    contents: [
      <>
        Thank you for submitting your mailing address! You should receive your paper check within
        the next 7-10 business days. If you have any questions about your paper check, please
        contact <ContactInfo email={partnerEmail} variant="largeBody" />.
      </>,
    ],
  },
  [PaymentMethod.PhysicalCard]: {
    header: 'You have successfully claimed your physical prepaid card',
    contents: [
      <>
        Thank you for submitting your mailing address. You should receive your prepaid card within
        the next 7 business days. If you have questions regarding your prepaid card, please reach
        out to <UsioContact variant="largeBody" />.
      </>,
    ],
  },
  [PaymentMethod.VirtualCard]: {
    header:
      'We have received your claim. Please check your inbox for next steps from Akimbo Card to access your funds.',
    contents: [
      'Next Steps:',
      <SimpleList key="steps" space type="number">
        <SimpleListItem variant="largeBody">
          You should receive an email from Akimbo Card with a link to finish claiming your virtual
          prepaid card.
        </SimpleListItem>
        <SimpleListItem variant="largeBody">
          You will need to create an account via the Akimbo Now portal to access your funds.
        </SimpleListItem>
      </SimpleList>,
      <>
        If you have any questions about your prepaid card, please contact{' '}
        <UsioContact variant="largeBody" />.
      </>,
    ],
    icon: 'markEmailRead',
  },
  [PaymentMethod.Zelle]: {
    header: 'Your transfer has been successfully initiated',
    contents: [
      'Your Zelle transfer has been initiated. You should have received an email confirmation from Zelle. Your funds should be available within minutes.',
      <>
        If you have any questions or concerns, you can contact Zelle at{' '}
        <ContactInfo
          email="customerservice@zellepay.com"
          phone="+18444288542"
          variant="largeBody"
        />
        .
      </>,
    ],
  },
});

function Submission(): JSX.Element {
  const { fulfillment, method } = useClaim();
  const { email: partnerEmail } = usePartner();
  const { header, contents, icon } = submissionContent(partnerEmail)[method];
  const history = useHistory();
  useCustomPageTitle(header);

  return (
    <div className="w-full flex flex-col items-center text-center">
      <div className="text-center pb-8">
        <Icon type={icon ?? 'checkCircle'} size="XXL" color={Color.Primary} />
      </div>
      <div className="w-3/4 flex flex-col">
        <Typography variant="h1">{header}</Typography>
      </div>
      <div className="flex flex-col items-center">
        <div
          className={cn('flex flex-col pt-11 pb-17 w-3/4 gap-y-8', {
            'text-start': method === PaymentMethod.VirtualCard,
          })}
        >
          {contents.map((body, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: const array, index won't change
            <Typography key={`content-${idx}`} variant="largeBody" tag="div">
              {body}
            </Typography>
          ))}
        </div>
        <div className="flex flex-col gap-y-4">
          <Button
            onClick={(): void =>
              history.push(
                makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId: fulfillment?.case?.id }),
              )
            }
          >
            View Application Outcome
          </Button>
          <Button
            onClick={(): void => history.push(ApplicantRoutes.APPLICATION_PORTAL)}
            variant="outline"
          >
            Back to Application Portal
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Submission;
